import { observable, configure, action, computed } from 'mobx';
import LocalModel from './localModel';

import util from 'preact-util';
import offlineRetryQueue from '../lib/offlineRetryQueue';
import * as offlineWriteQueue from '../lib/offlineWriteQueue';
import { isNetworkErrorEnvelope } from '../lib/offlineCache';

configure({ enforceActions: 'always' });

class VisitorStore extends LocalModel {
    constructor() {
        super('visitor', {
            namePlural: 'visitors',
            sort: '-createdDate',
            limit: 100,
            api: {
                search: {
                    url: '/api/visitors/',
                    params: {
                        limit: 15,
                        sort: '-createdDate',
                    },
                },
                load: {
                    url: '/api/visitors/',
                    params: {},
                },
                save: {
                    url: '/api/visitors/',
                    params: {},
                },
                delete: {
                    url: '/api/visitors/',
                    params: {},
                },
            },
        });
    }

    async saveField(id, field, value, updateMemory, opt = {}) {
        const response = await super.saveField(id, field, value, updateMemory, opt);
        if (response && response.message && response.message.indexOf('ERROR fetching API') !== -1) {
            offlineRetryQueue.addToQueue('/api/visitors/', id, field, value);
        }
        return response;
    }

    // Offline-aware POST. When the network fails, queue the body and
    // synthesize a 201 response shaped like the real one (`data.visitor.id`)
    // so existing call sites — which read `response.data.visitor.id` — keep
    // working with a tempId. The drain pass on reconnect resolves the
    // tempId to a real id and rewrites any dependent records (e.g. an
    // animal whose `owner` is this tempId).
    async insert(data, event) {
        const response = await super.insert(data, event);
        if (isNetworkErrorEnvelope(response)) {
            const tempId = await offlineWriteQueue.enqueueInsert({
                type: 'visitor',
                url: '/api/visitors/',
                body: data,
            });
            if (tempId == null) return response;
            return {
                status: 201,
                data: { visitor: { ...data, id: tempId, _pendingSync: true } },
            };
        }
        return response;
    }

    // Copy-paste customer import: preview classification (read-only).
    async importCsvPreview(csv, columnMapping = {}) {
        const response = await util.fetchApi('/api/visitors/csv/import-preview', { method: 'POST' }, { csv, columnMapping });
        return response;
    }

    // Copy-paste customer import: insert the selected new rows. Server re-dedups.
    async importCsvCommit(rows) {
        const response = await util.fetchApi('/api/visitors/csv/import', { method: 'POST' }, { rows });
        return response;
    }

    @observable newVisitor = {};

    @observable visitor = {};

    @observable visitors = [];

    @observable currentVisitors = [];

    @observable searchResult = [];

    @observable visitorAnimals = [];

    @observable visitorProducts = [];

    @observable visitorCalendars = [];

    @observable visitorHours = [];

    @observable visitorBookings = [];

    @observable animalSpecies = [];

    @observable animalBreeds = [];

    @observable veterinaries = [];

    @observable calendarEvent = {};

    @observable calendarEventStatus = null;

    @observable calendarEventMessage = null;

    @observable registerStatus = null;

    @observable registerResult = {};

    @observable jwtToken = util.get('visitorJwt') || 'null';

    @observable sendLoginCodeStatus = null;

    @observable sendLoginCodeMessage = null;

    // Visitors have no paidDate field — the old '-paidDate -createdDate' default
    // (copy-pasted from the salePayment store) forced the backend into an
    // in-memory sort on a nonexistent field for every visitor search/list.
    @observable sort = '-createdDate';

    @observable offset = 0;

    @observable limit = 100;

    @observable currentPage = 1;

    @action
    clearAll() {
        this.newVisitor = {};
        this.visitor = {};
        this.visitors = [];
        this.currentVisitors = [];
        this.searchResult = [];
        this.visitorAnimals = [];
        this.visitorProducts = [];
        this.visitorCalendars = [];
    }

    @action
    setSortField(field) {
        this.sort = field;
    }

    getVeterinary(id) {
        return this.veterinaries.find(vet => vet.id === id);
    }

    async loadVeterinaries() {
        const response = await util.fetchApi(`/api/users/veterinaries/`, { method: 'GET' }, {});
        switch (response.status) {
            case 200:
                this.updateKeyValue('veterinaries', response.data);
                return response;
        }
    }

    async searchVisitors({ search }) {
        const response = await util.fetchApi(`/api/visitors/`, { publish: true, method: 'GET' }, { search });
        console.log('searchVisitors', response);
        switch (response.status) {
            case 200:
                this.updateKeyValue('searchResult', response.data);
                return response;
            case 401:
                // PubSub.publish(topics.LOG_OUT);
                // route('/');
                break;
        }
    }

    async mergeVisitors({ currentVisitor, mergeWith }) {
        console.log('mergeVisitors', currentVisitor, mergeWith);
        const response = await util.fetchApi(`/api/visitors/mergeVisitors`, { publish: true, method: 'PATCH' }, {
            currentVisitor,
            mergeWith,
        });
        console.log('mergeVisitors', response);
        // Always return the response so the caller can distinguish a real merge
        // (status 202) from a rejection (e.g. 409 when a visitor is already
        // deactivated/merged) and avoid showing a false "merged" confirmation.
        return response;
    }

    async matchWithTripletex(visitorId) {
        const visitor = this.visitors.find(v => v.id === visitorId);
        if (!visitor) {
            return { status: 404, message: 'Visitor not found' };
        }
        // For single visitor search, still use API filtering
        const response = await util.fetchApi(`/api/integrations/tripletex/customers`, {
            method: 'GET',
        }, {
            cellphone: visitor.cellphone,
            email: visitor.email,
            orgNumber: visitor.orgNumber,
        });
        return response;
    }

    async loadAllTripletexCustomers() {
        // Fetch all customers from Tripletex (no filters)
        const response = await util.fetchApi(`/api/integrations/tripletex/customers`, {
            method: 'GET',
        }, {
            count: 10000, // Get all customers
        });
        return response;
    }

    matchVisitorsWithTripletexCustomers(visitors, tripletexCustomers) {
        // Match visitors with Tripletex customers locally
        // Priority: 1. orgNumber, 2. cellphone, 3. email
        const matches = {};

        // Normalize phone number for comparison
        const normalizePhone = (phone) => {
            if (!phone) return null;
            return phone.replace(/\D/g, '').replace(/^47/, ''); // Remove non-digits and leading 47
        };

        // Normalize email for comparison
        const normalizeEmail = (email) => {
            if (!email) return null;
            return email.toLowerCase().trim();
        };

        // Build lookup indexes for Tripletex customers
        const byOrgNumber = {};
        const byPhone = {};
        const byEmail = {};

        for (const tc of tripletexCustomers) {
            if (tc.organizationNumber) {
                const orgNum = tc.organizationNumber.replace(/\D/g, '');
                if (!byOrgNumber[orgNum]) byOrgNumber[orgNum] = [];
                byOrgNumber[orgNum].push(tc);
            }
            if (tc.phoneNumberMobile) {
                const phone = normalizePhone(tc.phoneNumberMobile);
                if (phone) {
                    if (!byPhone[phone]) byPhone[phone] = [];
                    byPhone[phone].push(tc);
                }
            }
            if (tc.phoneNumber) {
                const phone = normalizePhone(tc.phoneNumber);
                if (phone) {
                    if (!byPhone[phone]) byPhone[phone] = [];
                    byPhone[phone].push(tc);
                }
            }
            if (tc.email) {
                const email = normalizeEmail(tc.email);
                if (email) {
                    if (!byEmail[email]) byEmail[email] = [];
                    byEmail[email].push(tc);
                }
            }
        }

        // Match each visitor
        for (const visitor of visitors) {
            if (visitor.tripletexCustomerId) continue; // Skip already linked

            const visitorMatches = [];
            const seenIds = new Set();

            // 1. Match by orgNumber
            if (visitor.orgNumber) {
                const orgNum = visitor.orgNumber.replace(/\D/g, '');
                const found = byOrgNumber[orgNum] || [];
                for (const m of found) {
                    if (!seenIds.has(m.id)) {
                        seenIds.add(m.id);
                        visitorMatches.push(m);
                    }
                }
            }

            // 2. Match by cellphone
            if (visitor.cellphone) {
                const phone = normalizePhone(visitor.cellphone);
                if (phone) {
                    const found = byPhone[phone] || [];
                    for (const m of found) {
                        if (!seenIds.has(m.id)) {
                            seenIds.add(m.id);
                            visitorMatches.push(m);
                        }
                    }
                }
            }

            // 3. Match by email
            if (visitor.email) {
                const email = normalizeEmail(visitor.email);
                if (email) {
                    const found = byEmail[email] || [];
                    for (const m of found) {
                        if (!seenIds.has(m.id)) {
                            seenIds.add(m.id);
                            visitorMatches.push(m);
                        }
                    }
                }
            }

            if (visitorMatches.length > 0) {
                matches[visitor.id] = visitorMatches;
            }
        }

        return matches;
    }

    async saveTripletexCustomerId(visitorId, tripletexCustomerId) {
        const response = await util.fetchApi(`/api/visitors/${visitorId}`, {
            method: 'PATCH',
        }, {
            id: visitorId,
            tripletexCustomerId,
        });
        if (response.status === 202) {
            const idx = this.visitors.findIndex(v => v.id === visitorId);
            if (idx >= 0) {
                const newVisitors = [...this.visitors];
                newVisitors[idx] = { ...newVisitors[idx], tripletexCustomerId };
                this.updateKeyValue('visitors', newVisitors);
            }
        }
        return response;
    }

    async matchWithFiken(visitorId, fikenSlug) {
        const visitor = this.visitors.find(v => v.id === visitorId);
        if (!visitor) {
            return { status: 404, message: 'Visitor not found' };
        }
        const response = await util.fetchApi(`/api/integrations/fiken/companies/${fikenSlug}/contacts`, {
            method: 'GET',
        }, {
            phoneNumber: visitor.cellphone,
            email: visitor.email,
            organizationNumber: visitor.orgNumber,
        });
        return response;
    }

    async loadAllFikenContacts(fikenSlug) {
        const response = await util.fetchApi(`/api/integrations/fiken/companies/${fikenSlug}/contacts`, {
            method: 'GET',
        }, {});
        return response;
    }

    matchVisitorsWithFikenContacts(visitors, fikenContacts) {
        const matches = {};

        const normalizePhone = (phone) => {
            if (!phone) return null;
            return phone.replace(/\D/g, '').replace(/^47/, '');
        };

        const normalizeEmail = (email) => {
            if (!email) return null;
            return email.toLowerCase().trim();
        };

        const byOrgNumber = {};
        const byPhone = {};
        const byEmail = {};

        for (const fc of fikenContacts) {
            if (fc.organizationNumber) {
                const orgNum = fc.organizationNumber.replace(/\D/g, '');
                if (!byOrgNumber[orgNum]) byOrgNumber[orgNum] = [];
                byOrgNumber[orgNum].push(fc);
            }
            if (fc.phoneNumber) {
                const phone = normalizePhone(fc.phoneNumber);
                if (phone) {
                    if (!byPhone[phone]) byPhone[phone] = [];
                    byPhone[phone].push(fc);
                }
            }
            if (fc.email) {
                const email = normalizeEmail(fc.email);
                if (email) {
                    if (!byEmail[email]) byEmail[email] = [];
                    byEmail[email].push(fc);
                }
            }
        }

        for (const visitor of visitors) {
            if (visitor.fikenContactId) continue;

            const visitorMatches = [];
            const seenIds = new Set();

            if (visitor.orgNumber) {
                const orgNum = visitor.orgNumber.replace(/\D/g, '');
                const found = byOrgNumber[orgNum] || [];
                for (const m of found) {
                    if (!seenIds.has(m.contactId)) {
                        seenIds.add(m.contactId);
                        visitorMatches.push(m);
                    }
                }
            }

            if (visitor.cellphone) {
                const phone = normalizePhone(visitor.cellphone);
                if (phone) {
                    const found = byPhone[phone] || [];
                    for (const m of found) {
                        if (!seenIds.has(m.contactId)) {
                            seenIds.add(m.contactId);
                            visitorMatches.push(m);
                        }
                    }
                }
            }

            if (visitor.email) {
                const email = normalizeEmail(visitor.email);
                if (email) {
                    const found = byEmail[email] || [];
                    for (const m of found) {
                        if (!seenIds.has(m.contactId)) {
                            seenIds.add(m.contactId);
                            visitorMatches.push(m);
                        }
                    }
                }
            }

            if (visitorMatches.length > 0) {
                matches[visitor.id] = visitorMatches;
            }
        }

        return matches;
    }

    async saveFikenContactId(visitorId, fikenContactId) {
        const response = await util.fetchApi(`/api/visitors/${visitorId}`, {
            method: 'PATCH',
        }, {
            id: visitorId,
            fikenContactId,
        });
        if (response.status === 202) {
            const idx = this.visitors.findIndex(v => v.id === visitorId);
            if (idx >= 0) {
                const newVisitors = [...this.visitors];
                newVisitors[idx] = { ...newVisitors[idx], fikenContactId };
                this.updateKeyValue('visitors', newVisitors);
            }
        }
        return response;
    }

    async loadVisitorFromUser() {
        const response = await util.fetchApi(`/api/visitors/loadFromUser`, { publish: true, method: 'GET' }, {});
        return response?.data?.visitor;
    }

    async getInfo(props = {}) {
        const {
            customerId,
        } = props;

        const infoResponse = await util.fetchApi(`/api/visitors/info/${customerId}`, {
            jwtToken: this.jwtToken || 'null',
            method: 'GET',
        }, {});
        switch (infoResponse.status) {
            case 200:
                this.updateKeyValue('visitor', infoResponse.data);
                break;
            case 401:
                break;
            default:
                break;
        }
    }

    async sendLoginCodeSms({ cellphone, customer, captchaToken }) {
        const loginResponse = await util.fetchApi('/api/visitors/sendcode', {
            method: 'POST',
        }, {
            cellphone,
            customer,
            captchaToken,
        });
        this.updateKeyValue('sendLoginCodeStatus', loginResponse.status);
        switch (loginResponse.status) {
            case 200:
                this.updateKeyValue('sendLoginCodeMessage', 'Code sent to your phone.');
                return true;
            case 400:
                this.updateKeyValue('sendLoginCodeMessage', loginResponse.message || 'Captcha verification failed.');
                return { failed: true, status: loginResponse.status, message: loginResponse.message };
            case 500:
                this.updateKeyValue('sendLoginCodeMessage', loginResponse.message || 'Captcha verification error.');
                return { failed: true, status: loginResponse.status, message: loginResponse.message };
            default:
                return { failed: true, status: loginResponse.status, message: loginResponse.message };
        }
    }

    logout() {
        this.updateKeyValue('visitor', {});
        this.updateKeyValue('jwtToken', 'null');
        localStorage.removeItem('visitorJwt');
        localStorage.removeItem('visitorCellphone');
    }

    async validateLoginCodePhone(cellphone, code, language = 'en') {
        const isValidCode = util.validateCode(code, 1000, 999999);
        if (isValidCode) {
            const loginResponse = await util.fetchApi('/api/visitors/login/code/phone', {
                method: 'POST',
            }, { cellphone, code });
            switch (loginResponse.status) {
                case 200:
                    this.updateKeyValue('visitor', loginResponse.data.visitor);
                    localStorage.setItem('visitorJwt', loginResponse.data.apiToken);
                    this.updateKeyValue('jwtToken', loginResponse.data.apiToken);
                    return true;
                    break;
                case 403:
                    if (loginResponse.message === 'Wrong code!') {
                        this.updateObjectKeyValue('login', 'errorIcon', 'fas fa-exclamation-triangle');
                        if (language === 'no') {
                            this.updateObjectKeyValue('login', 'error', `Feil code! Husk at du må bruke den nyeste e-posten med koder.`);
                        } else {
                            this.updateObjectKeyValue('login', 'error', `Wrong code! Remember to use the latest email.`);
                        }
                    } else if (loginResponse.message === 'Login code has expired!') {
                        this.updateObjectKeyValue('loginLink', 'emailSent', false);
                        this.updateObjectKeyValue('login', 'errorIcon', 'fas fa-exclamation-triangle');
                        if (language === 'no') {
                            this.updateObjectKeyValue('login', 'error', `Koden har gått ut på dato. Be om en ny og forsøk igjen.`);
                        } else {
                            this.updateObjectKeyValue('login', 'error', `Code expired. Try to get a new code and try again.`);
                        }
                    } else {
                        this.updateObjectKeyValue('login', 'errorIcon', 'fas fa-bomb');
                        if (language === 'no') {
                            this.updateObjectKeyValue('login', 'error', `Det er noe feil med koden din. Du kan forsøke å be om en ny og prøve igjen.`);
                        } else {
                            this.updateObjectKeyValue('login', 'error', `Something wrong with your code! Try to get a new code and try again.`);
                        }
                    }
                    return false;
                    break;
                case 422:
                    this.updateObjectKeyValue('login', 'errorIcon', 'fas fa-exclamation-triangle');
                    if (language === 'no') {
                        this.updateObjectKeyValue('login', 'error', `Innloggingen feilet! Mangler info i noen av feltene under.`);
                    } else {
                        this.updateObjectKeyValue('login', 'error', `Login failed! Missing information.`);
                    }
                    return false;
                    break;
                default:
                    this.updateObjectKeyValue('login', 'errorIcon', 'fas fa-bomb');
                    if (language === 'no') {
                        this.updateObjectKeyValue('login', 'error', `Innloggingen feilet! ${loginResponse.status}: ${loginResponse.message}`);
                    } else {
                        this.updateObjectKeyValue('login', 'error', `Login failed! ${loginResponse.status}: ${loginResponse.message}`);
                    }
                    return false;
                    break;
            }
        }
    }

    async loadAnimals() {
        if (!this.visitor.id) {
            return;
        }
        const response = await util.fetchApi(`/api/visitors/animals`, {
            jwtToken: this.jwtToken || 'null',
            method: 'GET',
        }, {
            customer: this.visitor.customer,
        });
        switch (response.status) {
            case 200:
                this.updateKeyValue('visitorAnimals', response.data);
                if (response.included?.animalSpecies) {
                    this.updateKeyValue('animalSpecies', response.included.animalSpecies);
                }
                if (response.included?.animalBreeds) {
                    this.updateKeyValue('animalBreeds', response.included.animalBreeds);
                }
                break;
            default:
                break;
        }
    }

    async loadProducts({ calendar } = {}) {
        if (!this.visitor.id) {
            return;
        }
        // With a calendar the backend only returns products bookable in that
        // veterinary's calendar (product "Veterinærer" list, TICKET-01663).
        const response = await util.fetchApi(`/api/visitors/products`, {
            jwtToken: this.jwtToken || 'null',
            method: 'GET',
        }, {
            customer: this.visitor.customer,
            ...(calendar ? { calendar } : {}),
        });
        switch (response.status) {
            case 200:
                this.updateKeyValue('visitorProducts', response.data);
                break;
            default:
                break;
        }
    }

    async loadCalendars() {
        if (!this.visitor.id) {
            return;
        }
        const response = await util.fetchApi(`/api/visitors/calendars`, {
            jwtToken: this.jwtToken || 'null',
            method: 'GET',
        }, {
            customer: this.visitor.customer,
        });
        switch (response.status) {
            case 200:
                this.updateKeyValue('visitorCalendars', response.data);
                break;
            default:
                break;
        }
    }

    async loadHours({ product, calendar }) {
        if (!this.visitor.id) {
            return;
        }
        const response = await util.fetchApi(`/api/visitors/hours`, {
            jwtToken: this.jwtToken || 'null',
            method: 'GET',
        }, {
            customer: this.visitor.customer,
            product,
            calendar,
        });
        switch (response.status) {
            case 200:
                this.updateKeyValue('visitorHours', response.data);
                break;
            default:
                // Never keep the previous calendar's slots around: a stale
                // slot posted with the newly selected calendar id books an
                // hour that calendar never offered (TICKET-01352).
                this.updateKeyValue('visitorHours', []);
                break;
        }
    }

    async loadBookings() {
        if (!this.visitor.id) {
            return;
        }
        const response = await util.fetchApi(`/api/visitors/bookings`, {
            jwtToken: this.jwtToken || 'null',
            method: 'GET',
        }, {
            customer: this.visitor.customer,
        });
        switch (response.status) {
            case 200:
                this.updateKeyValue('visitorBookings', response.data);
                break;
            default:
                break;
        }
    }

    async placeBooking({ visitor, animal, calendar, product, hour }) {
        if (!this.visitor.id) {
            return;
        }
        const response = await util.fetchApi(`/api/visitors/booking/place`, {
            jwtToken: this.jwtToken || 'null',
            method: 'POST',
        }, {
            customer: this.visitor.customer,
            visitor,
            animal,
            product,
            calendar,
            hour,
        });
        switch (response.status) {
            case 200:
                this.updateKeyValue('calendarEvent', response.data);
                this.updateKeyValue('calendarEventStatus', response.status);
                break;
            case 409:
                this.updateKeyValue('calendarEventMessage', response.message);
                this.updateKeyValue('calendarEventStatus', response.status);
                break;
            default:
                // Surface unexpected failures (500, 404, network) so the form
                // can show an error and reset the chosen hour instead of
                // leaving the visitor without any feedback (TICKET-01352).
                this.updateKeyValue('calendarEventMessage', response.message || 'Unknown error');
                this.updateKeyValue('calendarEventStatus', response.status || 500);
                break;
        }
    }

    async registerSelf({ visitor, animals }) {
        if (!this.visitor.id) {
            return null;
        }
        const response = await util.fetchApi(`/api/visitors/register`, {
            jwtToken: this.jwtToken || 'null',
            method: 'POST',
        }, {
            customer: this.visitor.customer,
            visitor,
            animals,
        });
        this.updateKeyValue('registerStatus', response.status);
        if (response.status === 200) {
            this.updateKeyValue('registerResult', response.data);
        }
        return response;
    }

    async loadSelfRegistered() {
        return this.load({ query: { source: 'selfRegistered', noShowCount: 1 }, limit: 500, skipUpdate: false });
    }

    // SMS-utsendelse (Dyrejournal): counts for the current Besøkende filter.
    async bulkSmsPreview({ search = '', source = '', channel = 'sms' } = {}) {
        return util.fetchApi('/api/visitors/bulk-sms/preview', { method: 'POST' }, { search, source, channel });
    }

    // SMS-utsendelse (Dyrejournal): queue one job for the cron worker
    // (backend/bin/send-sms-bulk.js). Progress is read via smsBulkStore.
    async bulkSmsCreate({
        search = '', source = '', body = '', channel = 'sms', subject = '',
    } = {}) {
        return util.fetchApi('/api/visitors/bulk-sms', { method: 'POST' }, {
            search, source, body, channel, subject,
        });
    }
}

const store = new VisitorStore();
export default store;
